/**
 * 声音克隆的**场景模板**与朗读引导文案。
 *
 * 照 `minimax.cn/audio/voices-cloning` 那一页搬的（用户 2026-10-09 给了截图，
 * 原话「学一下」）。它解决的是「用户不知道该录什么」：克隆要求一段
 * **10 秒 ~ 5 分钟**的干净人声，而一个空白录音区前面站着的人多半会愣住。
 * 给一段现成的话照着念，是这一步最低的摩擦。
 *
 * ⚠️ **两处文本别混**（同名不同物，是这一页最容易接错的一根线）：
 *
 *   · **朗读引导**（`CLONE_SCENES` 里的 `lines`）—— 录参考音频时照着念的那段。
 *     它**不进后端**，纯粹是界面上的提词器。
 *   · **试听文本**（`DEFAULT_PREVIEW_TEXT` / 界面上的 `previewText`）——
 *     音色建好之后，用这句去合成一段来看效果。它**会**进 `inputs.previewText`，
 *     由后端转给上游当 `text`，决定 `demo_audio` 念什么。
 *
 * 把试听文本当成朗读引导发给上游，症状是「预览音频念的是试听那句、不是我以为的」；
 * 反过来则是「用户念了一段，但预览里听不到」。
 */

export type CloneScene = {
  id: string
  label: string
  /** 该场景下的引导朗读文本。**每个场景给多条**，让「换一条」真的有东西可换。 */
  lines: string[]
}

/**
 * 六个场景 —— 与参考站逐一对应（有声读物 / 影视配音 / Vlog独白 /
 * 教育培训 / 电台播客 / 智能客服）。**「随机」不是场景**，它是「跨场景抽一条」
 * 这个动作，所以在 `pickScriptLine` 里用一个空 id 表示，不在这里占一条。
 *
 * 每段都刻意写成**能一口气念完、且念起来自然**的口语 —— 太书面的话，
 * 用户念出来会端着，而音色就是照着这个语调学的。
 */
export const CLONE_SCENES: CloneScene[] = [
  {
    id: 'audiobook',
    label: '有声读物',
    lines: [
      '朋友给我寄了一个小礼物，是他出去旅游的时候买的。不是什么贵的东西，就是一个小小的摆件。收到的时候很开心，把它放在了书桌上。',
      '那年冬天来得特别早。十一月的风一吹，梧桐叶就落了一地，踩上去沙沙地响，像谁在身后轻轻跟着你走。',
    ],
  },
  {
    id: 'dubbing',
    label: '影视配音',
    lines: [
      '你听，外面的风停了。有些事，过去了就过去了，别再回头看了。',
      '我一直以为，只要跑得够快，就能把昨天甩在身后。后来才明白，昨天一直都在。',
    ],
  },
  {
    id: 'vlog',
    label: 'Vlog独白',
    lines: [
      '今天起了个大早，赶在太阳出来之前出门。街上还没什么人，风是凉的，特别舒服。',
      '这家店我路过了很多次，今天终于进去了。里面比想象中小，但每一个角落都很用心。',
    ],
  },
  {
    id: 'education',
    label: '教育培训',
    lines: [
      '各位同学，这一节我们讲一个很基础、但特别重要的概念。请先把笔记翻到第三页，我们边看边讲。',
      '在开始之前，我想先强调一点：方法比结论重要，步骤比答案重要。',
    ],
  },
  {
    id: 'podcast',
    label: '电台播客',
    lines: [
      '欢迎回到这一期的节目。今天想跟你聊一个话题 —— 关于怎么把一件小事，坚持着做下去。',
      '晚上好，这里是深夜频道。接下来这首歌，送给还没有睡的你。',
    ],
  },
  {
    id: 'service',
    label: '智能客服',
    lines: [
      '您好，很高兴为您服务。关于您反馈的物流问题，我这边已经帮您加急查询了，请您稍等片刻。',
      '您好，您反馈的问题我们已经收到了，正在为您处理。给您带来不便，非常抱歉。',
    ],
  },
]

/**
 * 跨全部场景抽一条引导文案。
 *
 * `sceneId` 传空串 = 「随机」（跨场景抽）；否则只在该场景内抽。
 * `avoid` 是「刚抽到的那条」—— 传进来的话会尽量换一条，
 * 否则用户连点两下「换一条」很可能两次都在原地（场景只有两条时尤其明显）。
 */
export function pickScriptLine(sceneId: string, avoid?: string): { sceneId: string; text: string } {
  const pool = sceneId ? CLONE_SCENES.filter((s) => s.id === sceneId) : CLONE_SCENES
  const scenes = pool.length ? pool : CLONE_SCENES
  const flat = scenes.flatMap((s) => s.lines.map((text) => ({ sceneId: s.id, text })))
  const rest = flat.filter((it) => it.text !== avoid)
  const bag = rest.length ? rest : flat
  return bag[Math.floor(Math.random() * bag.length)]
}

/**
 * 默认试听文本。选一句**中性、不涉及具体商品**的话 ——
 * 它是给所有人生成的第一段试听，不该预设立场（参考站那句也是这个路子）。
 */
export const DEFAULT_PREVIEW_TEXT =
  '您好，很高兴为您提供配音服务。这是一段试听文本，您可以听听看这条音色的效果。'

/** 试听文本上限（与参考站那一栏一致：300 字符）。前端 `maxLength` 与字数分母共用它。 */
export const PREVIEW_MAX_CHARS = 300

/** 参考音频的时长区间 —— **上游的硬约束**，后端 `app/audio.py` 里有同一组常量。 */
export const REF_MIN_SECONDS = 10
export const REF_MAX_SECONDS = 300
